import { useMemo } from "react";
import { type Allowance } from "@kanpla/api-contract";

import { type PosProduct } from "~/types/product";
import { useUserProductsPos } from "~/api/products/hooks/use-user-products";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { useShowPricesInclVat } from "~/hooks/use-show-prices-incl-vat";
import type { BasketStore } from "~/providers/store/basket";
import { calculateUserLimits } from "~/utils/user-limits/calculate-user-limits";
import { type UserLimitClient } from "~/utils/user-limits/types";
import { calculateAllowance } from "~/utils/allowance/calculate-allowance";

import { type PosProductInBasket } from "../../types";
import { useBasket } from "../use-basket";

import { computeProductsInBasket } from "./compute-products-in-basket";
import { parseInProductsBasic } from "./parse-in-products-basic";
import { parseInProductsPos } from "./parse-in-products-pos";

export type UseBasketProductsReturn = {
  basket: BasketStore["products"];
  products: PosProduct[];
  productsInBasket: PosProductInBasket[];
  isEmpty: boolean;
  totalUnit: number;
  totalUnitDiscounted: number;
  totalUnitExclVatDiscounted: number;
  totalDeposit: number;
  totalDepositExclVat: number;
  totalOrderDiscount: number;
  totalOrderDiscountable: number;
  productsStatus: "success" | "error" | "pending";
  allowances: Allowance[];
  userLimits: UserLimitClient[];
  defaultTaxData: {
    taxRate: number;
    taxRateName: string | null;
    taxRateId: string | null;
  };
};

/**
 * Hook to get the products in the basket
 * @returns {UseBasketProductsReturn}
 */
export function useBasketProducts(): UseBasketProductsReturn {
  const { locale } = useCurrentTillLocalisation();
  const { data, status: userProductsStatus } = useUserProductsPos();

  const { products: basket, orderDiscount } = useBasket();

  const { status: tillStatus } = useCurrentTill();
  const showPricesInclVat = useShowPricesInclVat();

  return useMemo(() => {
    if (userProductsStatus !== "success" || tillStatus !== "success") {
      return {
        basket,
        products: [],
        isEmpty: true,
        totalUnit: 0,
        totalUnitDiscounted: 0,
        totalUnitExclVatDiscounted: 0,
        totalDeposit: 0,
        totalDepositExclVat: 0,
        totalOrderDiscount: 0,
        totalOrderDiscountable: 0,
        productsStatus: userProductsStatus,
        productsInBasket: [],
        allowances: [],
        userLimits: [],
        defaultTaxData: {
          taxRate: 0.25,
          taxRateName: null,
          taxRateId: null
        }
      };
    }

    const {
      categories,
      currency,
      defaultTaxRate,
      discounts,
      products: allProducts,
      user
    } = data;

    const allowances = user?.allowances ?? [];

    const userLimits = calculateUserLimits({
      userLimits: user?.userLimits ?? [],
      basket,
      products: allProducts
    });

    const productsBasic = parseInProductsBasic({
      products: allProducts,
      categories
    });

    const updatedAllowances = calculateAllowance({
      products: productsBasic,
      allowances,
      discounts,
      basket
    });

    const products = parseInProductsPos({
      products: productsBasic,
      allowances: updatedAllowances,
      locale,
      basket,
      currency,
      discounts,
      showPricesInclVat
    });

    const {
      productsInBasket: productsInBasketWithDiscount,
      totalUnit,
      totalUnitDiscounted,
      totalUnitExclVatDiscounted,
      totalDeposit,
      totalDepositExclVat,
      totalOrderDiscount,
      totalOrderDiscountable
    } = computeProductsInBasket({
      basket,
      products,
      discounts,
      allowances,
      orderDiscount,
      defaultTaxRate,
      currency,
      locale,
      showPricesInclVat
    });

    return {
      basket,

      products,
      productsStatus: userProductsStatus,
      productsInBasket: productsInBasketWithDiscount,
      isEmpty: !Object.keys(basket).length,
      totalUnit,
      totalUnitDiscounted,
      totalUnitExclVatDiscounted,
      totalDeposit,
      totalDepositExclVat,
      totalOrderDiscount,
      totalOrderDiscountable,

      allowances: updatedAllowances,

      userLimits,

      defaultTaxData: {
        taxRate: defaultTaxRate.vatRate,
        taxRateName: defaultTaxRate.name,
        taxRateId: defaultTaxRate.id
      }
    };
  }, [
    userProductsStatus,
    tillStatus,
    showPricesInclVat,
    data,
    basket,
    locale,
    orderDiscount
  ]);
}
